import type { PricingSettings } from '@/types'

const DEFAULTS: PricingSettings = {
  tax_rate_cash: 0.15,
  tax_rate_card: 0.05,
  tax_rate_online: 0.15,
  tenant_jurisdiction: '',
  pra_rims_live: false,
  delivery_excluded_from_tax_base: false,
  pos_service_fee: 0,
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export function mergePricingSettings(p?: Partial<PricingSettings> | null): PricingSettings {
  return {
    tax_rate_cash: p?.tax_rate_cash ?? DEFAULTS.tax_rate_cash,
    tax_rate_card: p?.tax_rate_card ?? DEFAULTS.tax_rate_card,
    tax_rate_online: p?.tax_rate_online ?? DEFAULTS.tax_rate_online,
    tenant_jurisdiction: p?.tenant_jurisdiction ?? DEFAULTS.tenant_jurisdiction,
    pra_rims_live: p?.pra_rims_live ?? DEFAULTS.pra_rims_live,
    delivery_excluded_from_tax_base:
      p?.delivery_excluded_from_tax_base ?? DEFAULTS.delivery_excluded_from_tax_base,
    // F-MM-N9: already resolved server-side against the enable flag and the
    // tenant's fiscal authority, so it is 0 for PRA and opted-out tenants.
    // The counter applies no gate of its own — it just renders what it is told.
    pos_service_fee: p?.pos_service_fee ?? DEFAULTS.pos_service_fee,
    // Pass through the active online sub-methods (migration 050). Does not
    // affect any tax math — computeCartTotals still keys purely on the coarse
    // 'online' intent — it just rides along for the tender pad to render.
    online_submethods: p?.online_submethods ?? [],
  }
}

export interface OrderPricingExtras {
  /** Flat delivery add-on. Included in tax base unless DeliveryExcludedFromTaxBase is true. */
  deliveryFee?: number
  /**
   * Sum of per-line manual discounts on the order. Reduces the taxable
   * base alongside the bill-level `discount`. Stacks with `discount` —
   * the cashier may apply both. Defaults to 0.
   */
  itemDiscountsTotal?: number
}

export function computeCartTotals(
  subtotal: number,
  discount: number,
  intent: 'cash' | 'card' | 'online',
  p: PricingSettings,
  extras?: OrderPricingExtras
) {
  const deliveryFee = round2(Math.max(0, extras?.deliveryFee ?? 0))
  const itemDiscounts = round2(Math.max(0, extras?.itemDiscountsTotal ?? 0))
  const taxable = Math.max(0, subtotal - discount - itemDiscounts)

  // Tax base: the discounted goods value plus delivery by default. Tenants
  // who have explicitly opted delivery out of the base flip the setting.
  // Mirrors backend pricing.ComputeTotalsEx. (F-RETAIL-04: service charge
  // was removed from the model entirely — there is no service component.)
  let taxBase = taxable
  if (!p.delivery_excluded_from_tax_base) taxBase += deliveryFee

  // Rate is purely the configured per-intent rate. Mirrors backend
  // pricing.TaxRateForCheckoutIntent. The Punjab/RIMS gate that used to
  // override card+online down to the cash rate was removed 2026-08-03 —
  // it made the counter disagree with the Settings screen with no
  // indication why. A tenant charges what it is configured to charge.
  const tr =
    intent === 'card'
      ? p.tax_rate_card
      : intent === 'online'
        ? p.tax_rate_online
        : p.tax_rate_cash

  const tax = round2(taxBase * tr)

  // F-MM-N9: the FBR POS service fee. Collected on FBR's behalf rather than
  // being consideration for a supply, so it is deliberately absent from
  // taxBase above and enters only the amount the customer pays. Mirrors
  // backend pricing.ComputeTotalsEx — if these two disagree, the counter shows
  // one price and the backend charges another.
  //
  // Flat per invoice: it does not scale with the bill, does not vary by
  // intent, and is not skipped on a fully-discounted order.
  const posFee = round2(Math.max(0, p.pos_service_fee ?? 0))

  const total = round2(taxable + tax + deliveryFee + posFee)
  // `taxRate` is returned as a fraction (0.16 == 16%) so the caller can
  // render a consistent "Sales Tax (16%)" label alongside the money amount.
  return {
    taxable,
    tax,
    posFee,
    total,
    taxRate: tr,
    delivery: deliveryFee,
    itemDiscounts,
  }
}
